// Drag-to-reorder for the Forecast Manager's cards: press a card and move to pick it up (or
// press and hold on touch), drag it — the cards it passes slide aside to show where it will
// land — and release anywhere in its section. Esc cancels.
//
// Stacking (when the caller passes onStack): hold the dragged card over the MIDDLE of another
// card and it outlines as a stack target; release to stack the two. A card dragged out of an
// open stack and released outside it leaves the stack (onUnstack).
//
// It runs as a small imperative controller on the cards' own DOM nodes rather than React
// state: the drag moves every frame, and transforms written straight to the nodes keep it
// smooth without re-rendering the list. The row components stay plain functions of their
// props (the tests call them directly), and only attach a press handler.
import { sortShift, sortTargetIndex } from "../lib/forecast/sortOrder.js";

/** Pointer travel that turns a mouse press into a drag, so a plain click still selects. */
const MOUSE_START_PX = 5;
/** On touch, a press-and-hold picks the card up; moving first scrolls the list instead. */
const TOUCH_HOLD_MS = 350;
const TOUCH_SLOP_PX = 8;
/** Auto-scroll: within this many px of the list's top/bottom edge, up to this many px a frame. */
const EDGE_PX = 40;
const EDGE_SPEED = 12;
/** How the passed cards slide aside, and how a cancelled card returns. */
const SLIDE = "transform 150ms ease";
/** Over the middle this share of a card's height, held this long, makes it a stack target. */
const STACK_BAND = 0.25;
const STACK_DWELL_MS = 300;

let session = null;

/**
 * Props for a card's outer element. `section` keeps a drag inside its own list (Watchlist,
 * Recent, or `stack:<id>` for an open stack's cards); `onReorder(fromId, toId)` moves
 * `fromId` to `toId`'s place, as before.
 *
 * @param {string} id
 * @param {string} section
 * @param {((fromId: string, toId: string) => void) | undefined} onReorder
 * @param {{onStack?: Function, onUnstack?: Function, isStack?: boolean}} [opts] stacking:
 *   `onStack(cardId, targetId)` on a drop onto a card's middle (not offered while dragging a
 *   whole stack); `onUnstack(cardId)` on a drop outside the card's open stack
 */
export function sortableRowProps(id, section, onReorder, opts = {}) {
  if (!onReorder) return {};
  return {
    "data-sortable-id": id,
    "data-sortable-section": section,
    onPointerDown: (e) => press(e, id, section, onReorder, opts),
  };
}

function press(e, id, section, onReorder, opts) {
  if (session || (e.pointerType === "mouse" && e.button !== 0)) return;
  // The card's own controls (watch, settings, share) keep their clicks.
  if (e.target.closest("button, a, input, select, textarea, [role='button']")) return;
  const el = e.currentTarget;
  session = {
    id,
    section,
    onReorder,
    onStack: opts.isStack ? null : opts.onStack || null,
    onUnstack: opts.onUnstack || null,
    el,
    touch: e.pointerType === "touch",
    startX: e.clientX,
    startY: e.clientY,
    lastY: e.clientY,
    active: false,
    holdTimer: null,
  };
  if (session.touch) {
    session.holdTimer = setTimeout(() => session && activate(), TOUCH_HOLD_MS);
  }
  window.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", onUp);
  window.addEventListener("pointercancel", onCancel);
  // telemetry-gesture: Escape cancels a drag in progress; the drop emits the reorder or stack intent
  window.addEventListener("keydown", onKey);
  // A pointer release is not guaranteed once the window loses focus: cancel, or the drag would
  // stay "on" and block every later one.
  window.addEventListener("blur", onCancel);
}

function scrollParent(el) {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if (oy === "auto" || oy === "scroll") return p;
  }
  return null;
}

function activate() {
  const s = session;
  const rows = [...document.querySelectorAll(`[data-sortable-section="${s.section}"]`)];
  s.rows = rows;
  s.from = rows.indexOf(s.el);
  if (s.from < 0) return teardown(false);
  // The app root is CSS-zoomed: pointer px divide by this to become the cards' CSS px.
  s.zoom = s.el.offsetHeight ? s.el.getBoundingClientRect().height / s.el.offsetHeight : 1;
  s.mids = rows.map((r) => r.offsetTop + r.offsetHeight / 2);
  s.heights = rows.map((r) => r.offsetHeight);
  s.stackGroup = s.onUnstack ? s.el.closest("[data-stack-group]") : null;
  s.candidate = -1;
  s.stackTarget = -1;
  s.outside = false;
  s.step = s.el.offsetHeight + (parseFloat(getComputedStyle(s.el).marginBottom) || 0);
  s.scroller = scrollParent(s.el);
  s.scrollStart = s.scroller ? s.scroller.scrollTop : 0;
  s.to = s.from;
  s.active = true;
  for (const r of rows) r.style.transition = r === s.el ? "none" : SLIDE;
  s.el.style.zIndex = "10";
  s.el.style.filter = "drop-shadow(0 6px 14px rgba(0,0,0,0.45))";
  document.body.classList.add("sortable-dragging");
  // Once a touch drag has begun, the finger moves the card, not the list.
  window.addEventListener("touchmove", blockTouchScroll, { passive: false });
  s.raf = requestAnimationFrame(autoScroll);
  layout();
}

function layout() {
  const s = session;
  const scrolled = s.scroller ? s.scroller.scrollTop - s.scrollStart : 0;
  const dy = (s.lastY - s.startY) / s.zoom + scrolled;
  s.el.style.transform = `translateY(${dy}px) scale(1.02)`;
  const center = s.mids[s.from] + dy;
  // Dragged out of its open stack: it will leave the stack, so its old place stays shut.
  if (s.stackGroup) {
    const g = s.stackGroup.getBoundingClientRect();
    s.outside = s.lastY < g.top || s.lastY > g.bottom;
  }
  if (s.stackTarget < 0) s.to = s.outside ? s.from : sortTargetIndex(s.mids, s.from, center);
  const shifts = s.rows.map((r, j) => (r === s.el ? 0 : sortShift(j, s.from, s.to, s.step)));
  s.rows.forEach((r, j) => {
    if (r === s.el) return;
    r.style.transform = shifts[j] ? `translateY(${shifts[j]}px)` : "";
  });
  if (!s.onStack) return;
  // The card under the dragged one's center, where it is drawn now.
  let over = -1;
  s.rows.forEach((r, j) => {
    if (r === s.el) return;
    if (Math.abs(center - (s.mids[j] + shifts[j])) < s.heights[j] * STACK_BAND) over = j;
  });
  if (over !== s.candidate) {
    s.candidate = over;
    s.candidateSince = performance.now();
    setStackTarget(s, -1);
  }
}

function setStackTarget(s, j) {
  if (s.stackTarget === j) return;
  if (s.stackTarget >= 0) {
    const old = s.rows[s.stackTarget];
    old.style.outline = "";
    old.style.outlineOffset = "";
  }
  s.stackTarget = j;
  if (j >= 0) {
    s.rows[j].style.outline = "2px solid var(--c-accent)";
    s.rows[j].style.outlineOffset = "2px";
  }
}

function autoScroll() {
  const s = session;
  if (!s || !s.active) return;
  if (s.candidate >= 0 && s.stackTarget < 0) {
    if (performance.now() - s.candidateSince >= STACK_DWELL_MS) setStackTarget(s, s.candidate);
  }
  if (s.scroller) {
    const r = s.scroller.getBoundingClientRect();
    const speed =
      s.lastY < r.top + EDGE_PX
        ? -EDGE_SPEED * Math.min(1, (r.top + EDGE_PX - s.lastY) / EDGE_PX)
        : s.lastY > r.bottom - EDGE_PX
          ? EDGE_SPEED * Math.min(1, (s.lastY - (r.bottom - EDGE_PX)) / EDGE_PX)
          : 0;
    if (speed) {
      const before = s.scroller.scrollTop;
      s.scroller.scrollTop = before + speed;
      if (s.scroller.scrollTop !== before) layout();
    }
  }
  s.raf = requestAnimationFrame(autoScroll);
}

function onMove(e) {
  const s = session;
  if (!s) return;
  s.lastY = e.clientY;
  if (s.active) return layout();
  const dist = Math.hypot(e.clientX - s.startX, e.clientY - s.startY);
  if (s.touch) {
    // Moved before the hold completed: this is a scroll, not a drag.
    if (dist > TOUCH_SLOP_PX) teardown(false);
  } else if (dist > MOUSE_START_PX) {
    activate();
  }
}

function onUp() {
  teardown(true);
}

function onCancel() {
  teardown(false);
}

function onKey(e) {
  if (e.key === "Escape" && session) {
    e.preventDefault();
    teardown(false);
  }
}

function blockTouchScroll(e) {
  if (session?.active) e.preventDefault();
}

// The click that ends a drag must not also select the card under the pointer.
function swallowClick(e) {
  e.stopPropagation();
  e.preventDefault();
}

function teardown(commit) {
  const s = session;
  session = null;
  clearTimeout(s?.holdTimer);
  window.removeEventListener("pointermove", onMove);
  window.removeEventListener("pointerup", onUp);
  window.removeEventListener("pointercancel", onCancel);
  window.removeEventListener("keydown", onKey);
  window.removeEventListener("blur", onCancel);
  window.removeEventListener("touchmove", blockTouchScroll);
  if (!s || !s.active) return;
  cancelAnimationFrame(s.raf);
  document.body.classList.remove("sortable-dragging");
  window.addEventListener("click", swallowClick, { capture: true, once: true });
  setTimeout(() => window.removeEventListener("click", swallowClick, { capture: true }), 0);
  const stackOnto = commit && s.stackTarget >= 0 ? s.rows[s.stackTarget].dataset.sortableId : null;
  if (s.stackTarget >= 0) setStackTarget(s, -1);
  const unstack = commit && !stackOnto && s.outside;
  const moved = commit && !stackOnto && !unstack && s.to !== s.from;
  if (stackOnto || unstack) {
    for (const r of s.rows) {
      r.style.transition = "none";
      r.style.transform = "";
    }
    if (stackOnto) s.onStack(s.id, stackOnto);
    else s.onUnstack(s.id);
    requestAnimationFrame(() => {
      for (const r of s.rows) r.style.transition = "";
    });
  } else if (moved) {
    // The list re-renders in its new order; drop the transforms in the same frame, with no
    // transition, so the cards land where they were drawn instead of animating back.
    for (const r of s.rows) {
      r.style.transition = "none";
      r.style.transform = "";
    }
    s.onReorder(s.id, s.rows[s.to].dataset.sortableId);
    requestAnimationFrame(() => {
      for (const r of s.rows) r.style.transition = "";
    });
  } else {
    // Cancelled or dropped where it started: everything glides back.
    for (const r of s.rows) {
      r.style.transition = SLIDE;
      r.style.transform = "";
    }
    setTimeout(() => {
      for (const r of s.rows) r.style.transition = "";
    }, 160);
  }
  s.el.style.zIndex = "";
  s.el.style.filter = "";
}
